// How the undo stack's ports read, list and delete rows through v1.

import type { components } from '@/generated/contracts/api-v1';
import { toResult, type Fetched } from '@/infrastructure/api/v1/client';
import { listAll } from '@/infrastructure/api/v1/lists';
import type { SceneSync } from './scenePorts';

type Row = Record<string, unknown>;
type DeletionDto = components['schemas']['DeletionDto'];

/** The fields of `row` that `fields` names, as a create or patch body takes them. */
export const pick = (row: object, fields: readonly string[]): Row =>
  Object.fromEntries(fields.filter(field => field in row).map(field => [field, (row as Row)[field]]));

/** The data of a call, or its problem thrown. */
export async function read<T>(pending: Promise<Fetched<T>>): Promise<T> {
  const { data, error } = await toResult(pending);
  if (error) throw error;
  return data;
}

/** A delete's deletion, or null when the row was gone already (404); any other problem thrown. */
export async function remove(pending: Promise<Fetched<DeletionDto>>): Promise<DeletionDto | null> {
  const { data, error } = await toResult(pending);
  if (error?.status === 404) return null;
  if (error) throw error;
  return data;
}

/**
 * A delete whose rows (the one named and what went with it) leave the renderer and the editor through `sync`.
 * Resolves the deletion's id, or null when the row was gone already.
 */
export async function removeRow(sync: SceneSync, pending: Promise<Fetched<DeletionDto>>): Promise<string | null> {
  const deletion = await remove(pending);
  if (!deletion) return null;
  await sync.hideDeletion(deletion);
  return deletion.deletionUniqueId;
}

/** The row a read found, or null when it is gone (404); any other problem thrown. */
export async function readUnlessGone<T>(pending: Promise<Fetched<T>>): Promise<T | null> {
  const { data, error } = await toResult(pending);
  if (error?.status === 404) return null;
  if (error) throw error;
  return data;
}

/** Every item of a list, or its problem thrown. */
export async function list<T>(fetchPage: Parameters<typeof listAll<T>>[0]): Promise<T[]> {
  const { data, error } = await listAll(fetchPage);
  if (error) throw error;
  return data;
}
